import { useState, useEffect } from "react";

export default function PricingFilter({
	filters,
	setFilters,
}) {
	// Local state — inputs never touch `filters` until Apply is clicked.
	// This stops the API from firing on every keystroke.
	const [localPerPerson, setLocalPerPerson] = useState({
		enabled: !!filters.perPersonEnabled,
		min: filters.minSpendPerPerson?.min || "",
		max: filters.minSpendPerPerson?.max || "",
	});
	const [localVenueRental, setLocalVenueRental] = useState({
		enabled: !!filters.venueRentalEnabled,
		min: filters.venueRental?.min || "",
		max: filters.venueRental?.max || "",
	});

	// Sync local state when filters are cleared externally (e.g. Reset All)
	useEffect(() => {
		setLocalPerPerson({
			enabled: !!filters.perPersonEnabled,
			min: filters.minSpendPerPerson?.min || "",
			max: filters.minSpendPerPerson?.max || "",
		});
	}, [
		filters.perPersonEnabled,
		filters.minSpendPerPerson?.min,
		filters.minSpendPerPerson?.max,
	]);

	useEffect(() => {
		setLocalVenueRental({
			enabled: !!filters.venueRentalEnabled,
			min: filters.venueRental?.min || "",
			max: filters.venueRental?.max || "",
		});
	}, [
		filters.venueRentalEnabled,
		filters.venueRental?.min,
		filters.venueRental?.max,
	]);

	const handleApply = () => {
		setFilters((prev) => ({
			...prev,
			perPersonEnabled: localPerPerson.enabled,
			minSpendPerPerson: { min: localPerPerson.min, max: localPerPerson.max },
			venueRentalEnabled: localVenueRental.enabled,
			venueRental: { min: localVenueRental.min, max: localVenueRental.max },
		}));
	};

	return (
		<div className="space-y-4 p-4 border rounded shadow-sm bg-white">
			{/* Budget Per Person */}
			<div className="flex items-center gap-3">
				<input
					type="radio"
					name="spendOption"
					className="form-radio h-4 w-4 text-blue-600"
					checked={localPerPerson.enabled}
					onChange={() => {
						setLocalPerPerson((prev) => ({ ...prev, enabled: true }));
						setLocalVenueRental({ enabled: false, min: "", max: "" });
						// Clear venue rental from API immediately
						setFilters((prev) => ({
							...prev,
							venueRentalEnabled: false,
							venueRental: { min: "", max: "" },
						}));
					}}
				/>
				<label className="w-full">Budget Per Person</label>
				<div className="flex flex-1 items-center justify-end gap-3">
					<input
						type="number"
						placeholder="Min"
						value={localPerPerson.min}
						onChange={(e) => {
							const value = e.target.value;
							setLocalPerPerson((prev) => ({ ...prev, min: value }));
							if (value === "") setFilters((prev) => ({ ...prev, minSpendPerPerson: { ...prev.minSpendPerPerson, min: "" } }));
						}}
						disabled={!localPerPerson.enabled}
						className="border p-1 rounded w-20"
					/>
					<input
						type="number"
						placeholder="Max"
						value={localPerPerson.max}
						onChange={(e) => {
							const value = e.target.value;
							setLocalPerPerson((prev) => ({ ...prev, max: value }));
							if (value === "") setFilters((prev) => ({ ...prev, minSpendPerPerson: { ...prev.minSpendPerPerson, max: "" } }));
						}}
						disabled={!localPerPerson.enabled}
						className="border p-1 rounded w-20"
					/>
				</div>
			</div>

			{/* Budget Per Event */}
			<div className="flex items-center gap-3">
				<input
					type="radio"
					name="venueRentalToggle"
					className="form-radio h-4 w-4 text-blue-600"
					checked={localVenueRental.enabled}
					onChange={() => {
						setLocalVenueRental((prev) => ({ ...prev, enabled: true }));
						setLocalPerPerson({ enabled: false, min: "", max: "" });
						// Clear per person from API immediately
						setFilters((prev) => ({
							...prev,
							perPersonEnabled: false,
							minSpendPerPerson: { min: "", max: "" },
						}));
					}}
				/>
				<label className="w-full">Budget Per Event</label>
				<div className="flex flex-1 items-center justify-end gap-3">
					<input
						type="number"
						placeholder="Min"
						value={localVenueRental.min}
						onChange={(e) => {
							const value = e.target.value;
							setLocalVenueRental((prev) => ({ ...prev, min: value }));
							if (value === "") setFilters((prev) => ({ ...prev, venueRental: { ...prev.venueRental, min: "" } }));
						}}
						disabled={!localVenueRental.enabled}
						className="border p-1 rounded w-20"
					/>
					<input
						type="number"
						placeholder="Max"
						value={localVenueRental.max}
						onChange={(e) => {
							const value = e.target.value;
							setLocalVenueRental((prev) => ({ ...prev, max: value }));
							if (value === "") setFilters((prev) => ({ ...prev, venueRental: { ...prev.venueRental, max: "" } }));
						}}
						disabled={!localVenueRental.enabled}
						className="border p-1 rounded w-20"
					/>
				</div>
			</div>

			{/* Apply — visible only when a value has been entered */}
			{((localPerPerson.enabled && (localPerPerson.min || localPerPerson.max)) ||
				(localVenueRental.enabled && (localVenueRental.min || localVenueRental.max))) && (
				<div className="pt-1">
					<button
						type="button"
						onClick={handleApply}
						className="w-full bg-[#FE4747] text-white py-2 rounded-md text-sm hover:bg-red-600"
					>
						Apply
					</button>
				</div>
			)}
		</div>
	);
}
